import React, {PropsWithChildren} from "react"
import {AssignmentPageItemHeader} from "../AssignmentPageItemHeader/AssignmentPageItemHeader"
import {Assignee, AssignmentStage, AssignmentStatus} from "../../resources/assignments"
import classNames from "classnames"
import {AssignmentPageItemTitleAndDescription} from "../AssignmentPageItemTitleAndDescription/AssignmentPageItemTitleAndDescription"
import {Badge} from "../Badge/Badge"
import {IcomoonIconName} from "../Icon/icons"

export type AssignmentRiskFlagSeverity = "RiskFlag" | "AutoReject"

export type AssignmentRiskFlag = {
	name: string
	severity: AssignmentRiskFlagSeverity
	description?: string
}

export const AssignmentRiskSeverity = {
	RiskFlag: "RiskFlag" as AssignmentRiskFlagSeverity,
	AutoReject: "AutoReject" as AssignmentRiskFlagSeverity,
}

const getFlagClassName = (severity?: AssignmentRiskFlagSeverity): string => {
	switch (severity) {
		case AssignmentRiskSeverity.RiskFlag:
			return "assignment-page-item-header-flags-risk"
		case AssignmentRiskSeverity.AutoReject:
			return "assignment-page-item-header-flags-auto-reject"
		default:
			return ""
	}
}

interface AssignmentPageItemProps {
	assignees: Assignee[]
	status: AssignmentStatus
	stage: AssignmentStage
	title: string
	description?: string | JSX.Element
	createdAt: Date
	statusUpdatedAt?: Date
	isOwnersItem?: boolean
	disabled?: boolean
	pageItemFooter?: JSX.Element
	pageCompletedItemHeader: JSX.Element
	assignmentFlags?: AssignmentRiskFlag[]
	escalatedBadge?: React.ReactNode
}

export function AssignmentPageItem({
	assignees,
	status,
	stage,
	title,
	description,
	createdAt,
	statusUpdatedAt,
	isOwnersItem,
	pageItemFooter,
	pageCompletedItemHeader,
	disabled,
	children,
	assignmentFlags,
	escalatedBadge,
}: PropsWithChildren<AssignmentPageItemProps>) {
	const isAssignmentCompleted = stage === AssignmentStage.Completed
	const headerToRender = isAssignmentCompleted ? (
		pageCompletedItemHeader
	) : (
		<AssignmentPageItemHeader assignees={assignees} status={status} escalatedBadge={escalatedBadge} />
	)

	const getFlagIcon = (severity?: AssignmentRiskFlagSeverity): IcomoonIconName | undefined => {
		switch (severity) {
			case AssignmentRiskSeverity.RiskFlag:
				return "award-flag-rewards-rating-filled"
			case AssignmentRiskSeverity.AutoReject:
				return "award-flag-rewards-rating-filled"
			default:
				return undefined
		}
	}

	return (
		<section className="assignment-page-item">
			<div className="assignment-page-item-header">
				<div className="assignment-page-item-title-and-description-container">
					<div className="assignment-page-item-title-row">
						<AssignmentPageItemTitleAndDescription
							stage={stage}
							title={title}
							description={description}
							createdAt={createdAt}
							statusUpdatedAt={statusUpdatedAt}
							isOwnersItem={isOwnersItem}
							disabled={disabled}
						/>
						<div className={classNames("assignment-page-item-header-metadata")}>{headerToRender}</div>
					</div>
					{assignmentFlags && assignmentFlags.length > 0 && (
						<div className={classNames("assignment-page-item-header-flags")}>
							{assignmentFlags.map((flag, idx) => (
								<Badge
									key={`${flag.name}/${idx}`}
									text={flag.name}
									icon={getFlagIcon(flag.severity)}
									iconFirst={true}
									iconClassName={getFlagClassName(flag.severity)}
									tooltipKey={`${flag.name}-tooltip`}
									tooltip={flag.description}
									tooltipPlace="right"
									tooltipClassname="assignment-page-item-header-flags-tooltip"
								/>
							))}
						</div>
					)}
				</div>
			</div>
			<div className={classNames("assignment-page-item-content", !pageItemFooter && "no-footer")}>{children}</div>
			{pageItemFooter && <div className="assignment-page-item-footer">{pageItemFooter}</div>}
		</section>
	)
}
